import type { LoanApplicationStatus } from '@paintscout/wisetack'

/**
 * Statuses where the borrower has accepted loan terms. Wisetack also sends approvedLoanAmount on
 * AUTHORIZED, ACTIONS REQUIRED and DECLINED, so the amount alone must never produce "locked in"
 * copy (ENG-2733).
 */
const LOCKED_STATUSES: LoanApplicationStatus[] = ['LOAN TERMS ACCEPTED', 'LOAN CONFIRMED', 'SETTLED']

/** Statuses that end an application, completing "Your …" / "{name}'s …". */
const ENDED_STATUSES: Partial<Record<LoanApplicationStatus, string>> = {
  EXPIRED: 'financing application expired',
  CANCELED: 'financing application was canceled',
  REFUNDED: 'financing was refunded'
}

export function getWisetackApplicationBody({
  status,
  approvedAmount,
  maximumAmount,
  contactName,
  inPresentation,
  formatAmount
}: {
  status?: LoanApplicationStatus
  approvedAmount?: number
  maximumAmount?: number
  contactName: string
  inPresentation?: boolean
  formatAmount: (value: number) => string
}): string {
  if (status && LOCKED_STATUSES.includes(status) && approvedAmount) {
    return inPresentation
      ? `You have locked in financing for ${formatAmount(approvedAmount)}!`
      : `${contactName} has locked in financing for ${formatAmount(approvedAmount)}!`
  }

  if (status === 'DECLINED') {
    return inPresentation
      ? 'Your last financing application was not approved. You can apply again.'
      : `${contactName}'s financing application was declined. You can send a new application.`
  }

  const ended = status && ENDED_STATUSES[status]
  if (ended) {
    return inPresentation
      ? `Your ${ended}. You can apply again.`
      : `${contactName}'s ${ended}. You can send a new application.`
  }

  // In progress (INITIATED, ACTIONS REQUIRED, AUTHORIZED): the borrower has not accepted terms,
  // so any amount is only what they qualify for.
  const qualifiedAmount = maximumAmount || approvedAmount
  if (qualifiedAmount) {
    return inPresentation
      ? `You are qualified for up to ${formatAmount(qualifiedAmount)}.`
      : `${contactName} is qualified for up to ${formatAmount(qualifiedAmount)}.`
  }

  return inPresentation
    ? 'Check your eligibility in less than 60 seconds without impacting your credit score'
    : `Send ${contactName} a financing application to see how much they qualify for.`
}
